import axios from "axios";

// A request with no timeout never fails — it just never finishes, and the page
// that was waiting on it shows its spinner forever with no error and no way
// back except navigating away. That is exactly what Karen hit on a rural link
// (2026-08-04): "All Summary" stuck on Loading, fine after leaving and
// returning. 30 s is far longer than any real call — the slowest surface is
// ~0.9 s at full history — so this only ever fires on a request that has
// genuinely stalled, and it turns a dead page into an error you can retry.
const TIMEOUT_MS = 30000;

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  // Which page the request came from — the owner's Who's Online tab shows it.
  headers: typeof window !== "undefined" ? { "X-App-Page": window.location.pathname } : {},
  withCredentials: true,
  timeout: TIMEOUT_MS,
});

// Axios rejects for a DELIBERATE abort as well as for a failure, so anything
// that cancels a superseded request must tell the two apart — painting "Load
// failed" on a request we ourselves threw away is worse than the bug.
export function isAbortError(error) {
  return (
    axios.isCancel(error) ||
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError"
  );
}

// One place that turns an axios rejection into something worth reading. The
// server's own message wins when it sent one; a stall gets named as a stall,
// because "Unable to load" sends people looking for a bug in the report.
export function errorMessage(error, fallback = "Something went wrong.") {
  if (error?.code === TRANSFER_STALLED) {
    return "The transfer stopped part-way — check your connection and try again.";
  }
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return "The server took too long to answer — this is usually a slow connection. Try again.";
  }
  if (error?.code === "ERR_NETWORK") {
    return "Could not reach the server. Check your connection and try again.";
  }
  return error?.response?.data?.error || fallback;
}

// --- Bulk transfers (zip download, file upload) ------------------------------
//
// These get a STALL deadline instead of the whole-request one, and the
// difference is not academic. TIMEOUT_MS is a deadline on the ENTIRE exchange,
// which is the right rule for an API call and the wrong one for moving files:
// job 76124's Zip All is 349 files and 177 MB, which cannot land inside 30 s
// unless the line holds ~47 Mbit/s the whole way. On live it reached about 70%
// and axios aborted it (Robert, 2026-08-07). The archive had built perfectly —
// the failure was entirely on the way down — and because an abort carries no
// response body there was nothing to quote, so the banner fell back to "Unable
// to build the archive", which pointed at the one part that had worked.
//
// How long a transfer SHOULD take is a function of its size and the line speed,
// and neither says anything about whether the server is in trouble. What does
// say so is bytes having stopped arriving. So that is what is timed: the clock
// restarts on every progress event and only fires when nothing has moved for
// STALL_MS. A slow rural download runs as long as it needs; a dead one still
// fails, which is the property the 2026-08-04 timeout was added for.
//
// The abort is tagged, because an untagged one reads as a deliberate cancel to
// `isAbortError` and every caller drops those silently — a stalled upload would
// then look like nothing happened at all.
export const TRANSFER_STALLED = "ETRANSFERSTALL";
const STALL_MS = 45000;

// Exported for its tests — nothing outside this module calls it directly.
export async function transfer(run, { onProgress, direction }) {
  const controller = new AbortController();
  let timer = null;
  let stalled = false;
  const bump = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      stalled = true;
      controller.abort();
    }, STALL_MS);
  };
  const progressKey = direction === "up" ? "onUploadProgress" : "onDownloadProgress";
  bump();
  try {
    return await run({
      signal: controller.signal,
      timeout: 0, // the stall guard above replaces it — see the note
      [progressKey]: (event) => {
        bump();
        onProgress?.(event);
      },
    });
  } catch (error) {
    if (stalled) throw Object.assign(new Error("Transfer stalled."), { code: TRANSFER_STALLED });
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

// --- Reporting a failure the user was shown ----------------------------------
//
// The half of the error log that the server cannot see. Job 76124's Zip All is
// the case in point: the server built the archive, sent it, and logged nothing,
// because from where it stood nothing went wrong. The only place that knew was
// the browser putting a red banner on the screen.
//
// FIRE AND FORGET, ALWAYS. It is called from catch blocks that have already
// handled the user's side of the failure, and a reporter that can throw would
// turn a handled error into an unhandled one. Everything is swallowed, including
// being logged out, being offline, and the endpoint not existing at all.
//
// Not called for aborts — a superseded request is not a fault, and the log would
// fill with them every time somebody typed in a search box.
//
// PASS `message` WHENEVER THE CALLER KNOWS BETTER, and one whole class of caller
// does: anything using `responseType: "blob"`. A JSON error body on a blob
// request arrives as a Blob, and reading it needs an await that `errorMessage`
// — which is synchronous, and has to stay that way for the render paths that use
// it — cannot do. So it sees no message and falls back.
//
// That is not hypothetical. Robert zipped two files with no stored bytes on
// 2026-08-07: the server refused with a perfectly clear 404, the banner showed
// it, and the log recorded "Unknown failure" for the same event. A log of what
// users were shown has to record what they were SHOWN.
export function reportError(error, { action, claimId = null, page, message } = {}) {
  try {
    if (isAbortError(error)) return;
    apiClient
      .post("/error-log", {
        action,
        claim_id: claimId,
        page: page ?? (typeof window === "undefined" ? null : window.location?.pathname),
        message: message || errorMessage(error, "Unknown failure"),
        status: error?.response?.status ?? null,
        // The code names the SHAPE of the failure (a stall, an unreachable
        // server) where the message names its effect — worth keeping both, since
        // the message is what the user saw and the code is what to fix.
        detail: [error?.code, error?.stack].filter(Boolean).join("\n") || null,
      })
      .catch(() => {});
  } catch {
    /* never let reporting a failure become one */
  }
}

// Favourite jobs — the header chips (max 3, per user; services/favourites.js).
export async function listFavouriteJobs({ signal } = {}) {
  const response = await apiClient.get("/user/favourites", { signal });
  return response.data;
}
export async function addFavouriteJob(claimId) {
  const response = await apiClient.post("/user/favourites", { claim_id: claimId });
  return response.data;
}
export async function removeFavouriteJob(claimId) {
  const response = await apiClient.delete(`/user/favourites/${claimId}`);
  return response.data;
}

export async function getCurrentUser() {
  const response = await apiClient.get("/user");
  return response.data;
}

export async function login(username, password) {
  const response = await apiClient.post("/user/login", { username, password });
  return response.data;
}

export async function logout() {
  const response = await apiClient.post("/user/logout");
  return response.data;
}

export async function updateTheme(theme) {
  const response = await apiClient.put("/user", { theme });
  return response.data;
}

// Per-user page defaults (the seed a page's filters reach for on a fresh
// login). Server validates against an allowlist; returns the updated user.
export async function updatePageDefaults(pageDefaults) {
  const response = await apiClient.put("/user/page-defaults", pageDefaults);
  return response.data;
}

// listUsers() = user management (full rows, admin-scoped server-side).
// pco/inspectors modes are narrow name-only dropdown sources.
// `activeOnly` is the SEARCH-versus-ASSIGN distinction (Karen Sweatman + Robert,
// 2026-08-12): a search or report FILTER must not offer retired accounts, while a
// data page must still show the one the job already holds — users default to
// clicking Save rather than Cancel, so a name missing from the dropdown is a name
// silently cleared off the job. The server returns both and leaves the choice
// here on purpose, because one query cannot tell which it is being asked for
// (routes/user.js). Data pages therefore do NOT pass this; ClaimForm keeps the
// current value selectable and marks it "(inactive)".
export async function listUsers({ pco = false, inspectors = false, activeOnly = false, signal } = {}) {
  const params = pco ? { pco: 1 } : inspectors ? { inspectors: 1 } : {};
  const response = await apiClient.get("/user/users", { params, signal });
  return activeOnly ? response.data.filter((row) => row.is_active !== false) : response.data;
}

export async function createUser(user) {
  const response = await apiClient.post("/user/users", user);
  return response.data;
}

export async function updateUser(id, user) {
  const response = await apiClient.put(`/user/users/${id}`, user);
  return response.data;
}

// Who reports to this person. Its own call because it writes `managed_by` on
// OTHER people's rows, which the server does in one transaction — half-applying
// it would leave somebody reporting to nobody with nothing to say so.
export async function setUserManages(id, userIds) {
  const response = await apiClient.put(`/user/users/${id}/manages`, { user_ids: userIds });
  return response.data;
}

export async function deleteUser(id) {
  const response = await apiClient.delete(`/user/users/${id}`);
  return response.data;
}

// Mimic (System Owner only): become another user for this session.
export async function mimicUser(id) {
  const response = await apiClient.post(`/user/mimic/${id}`);
  return response.data;
}

export async function stopMimic() {
  const response = await apiClient.post("/user/mimic/stop");
  return response.data;
}

// --- Error log (admin) -------------------------------------------------------

export async function listErrorLog({ days = 30, limit = 200, source = "", signal } = {}) {
  return (
    await apiClient.get("/error-log", {
      params: { days, limit, ...(source ? { source } : {}) },
      signal,
    })
  ).data;
}
export async function getErrorLogSummary({ days = 30, signal } = {}) {
  return (await apiClient.get("/error-log/summary", { params: { days }, signal })).data;
}

// The header badge's number: CRITICAL rows in the last N days (admin only).
export async function getCriticalErrorCount({ days = 7, signal } = {}) {
  return (await apiClient.get("/error-log/critical-count", { params: { days }, signal })).data;
}
// Clears exactly the entries listed — never a filter, so it can only ever remove
// what the user was looking at. axios puts a DELETE body under `data`.
export async function clearErrorLogEntries(ids) {
  return (await apiClient.delete("/error-log", { data: { ids } })).data;
}

// --- System Data View (config-driven lookup entities) ----------------------

export async function listDataEntities() {
  const response = await apiClient.get("/system-data/entities");
  return response.data;
}

export async function listEntityRows(entityKey, { includeInactive = false, signal } = {}) {
  const response = await apiClient.get(`/system-data/${entityKey}`, {
    params: includeInactive ? { includeInactive: 1 } : {},
    signal,
  });
  return response.data;
}

export async function createEntityRow(entityKey, data) {
  const response = await apiClient.post(`/system-data/${entityKey}`, data);
  return response.data;
}

export async function updateEntityRow(entityKey, id, data) {
  const response = await apiClient.put(`/system-data/${entityKey}/${id}`, data);
  return response.data;
}

export async function setEntityRowActive(entityKey, id, isActive) {
  const response = await apiClient.patch(`/system-data/${entityKey}/${id}/active`, {
    is_active: isActive,
  });
  return response.data;
}

// Child rows of an entity row (e.g. a company's contacts).
export async function listEntityChildRows(entityKey, id, childKey) {
  const response = await apiClient.get(`/system-data/${entityKey}/${id}/${childKey}`);
  return response.data;
}

export async function createEntityChildRow(entityKey, id, childKey, data) {
  const response = await apiClient.post(`/system-data/${entityKey}/${id}/${childKey}`, data);
  return response.data;
}

export async function updateEntityChildRow(entityKey, id, childKey, childId, data) {
  const response = await apiClient.put(
    `/system-data/${entityKey}/${id}/${childKey}/${childId}`,
    data
  );
  return response.data;
}

export async function deleteEntityChildRow(entityKey, id, childKey, childId) {
  await apiClient.delete(`/system-data/${entityKey}/${id}/${childKey}/${childId}`);
}

// --- Claims + status engine -------------------------------------------------

export async function getHomeStats() {
  return (await apiClient.get("/claims/home-stats")).data;
}
export async function setHomeAnchor(value) {
  return (await apiClient.put("/settings/home-anchor", { value })).data;
}

export async function listClaimStatuses() {
  const response = await apiClient.get("/claims/statuses");
  return response.data;
}

// Returns { rows, total }. pageSize defaults to 100 (server caps at 500).
export async function searchClaims(filters = {}, { page = 1, pageSize = 100 } = {}) {
  const response = await apiClient.get("/claims", { params: { ...filters, page, pageSize } });
  return response.data;
}

export async function createClaim(data) {
  const response = await apiClient.post("/claims", data);
  return response.data;
}

export async function getClaim(id) {
  const response = await apiClient.get(`/claims/${id}`);
  return response.data;
}

export async function updateClaim(id, data) {
  const response = await apiClient.put(`/claims/${id}`, data);
  return response.data;
}

// X-Ref: { related, group }. `related` is 1.0's set (my children plus my
// parent); `group` is the rest of the cross-reference, shown behind a disclosure.
export async function getRelatedClaims(id) {
  const response = await apiClient.get(`/claims/${id}/related`);
  return response.data;
}

// System Settings (registry-driven): owner-scoped entries only come back for
// the System Owner — render whatever the server returns.
export async function listSystemSettings() {
  return (await apiClient.get("/settings/system")).data;
}
export async function setSystemSetting(key, value) {
  return (await apiClient.put(`/settings/system/${key}`, { value })).data;
}

export async function changeClaimStatus(id, payload) {
  const response = await apiClient.post(`/claims/${id}/status`, payload);
  return response.data;
}

export async function getClaimBadges(ids, { signal } = {}) {
  if (!ids || ids.length === 0) return {};
  const response = await apiClient.get(`/claims/badges`, { params: { ids: ids.join(",") }, signal });
  return response.data;
}

// Badge activity tracking (docs/badge-activity-tracking.md) — read side.
export async function getCompletionsReport(period = "week") {
  const response = await apiClient.get(`/badge-tracking/completions`, { params: { period } });
  return response.data;
}
// The signed-in user's own recent badge completions (for the search one-liner).
export async function getRecentCompletions(minutes = 15) {
  const response = await apiClient.get(`/badge-tracking/recent`, { params: { minutes } });
  return response.data;
}

// Flat list of needs-attention badge rules ({key,label,group}) for the search
// page's "Needs Attention" filter dropdown.
export async function listRuleOptions() {
  const response = await apiClient.get(`/claims/reports/rule-options`);
  return response.data;
}

export async function getRulesReport(group, params = {}, { signal } = {}) {
  const response = await apiClient.get(`/claims/reports/rules/${group}`, { params, signal });
  return response.data;
}

export async function getRuleJobs(group, ruleKey, params = {}, { signal } = {}) {
  const response = await apiClient.get(`/claims/reports/rules/${group}/${ruleKey}`, { params, signal });
  return response.data;
}

// Summary view (1.0's RDR per-person grid): per-rule counts grouped by person.
export async function getRulesSummary(group, params = {}, { signal } = {}) {
  const response = await apiClient.get(`/claims/reports/rules-summary/${group}`, { params, signal });
  return response.data;
}

export async function getQccReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/qcc`, { params });
  return response.data;
}

export async function listQcc(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/qcc`);
  return response.data;
}

export async function createQcc(claimId) {
  const response = await apiClient.post(`/claims/${claimId}/qcc`);
  return response.data;
}

export async function updateQcc(claimId, qccId, data) {
  const response = await apiClient.put(`/claims/${claimId}/qcc/${qccId}`, data);
  return response.data;
}

export async function deleteQcc(claimId, qccId) {
  const response = await apiClient.delete(`/claims/${claimId}/qcc/${qccId}`);
  return response.data;
}

// findingId tags the upload to one finding on the QCC (results only).
export async function attachQccFiles(claimId, qccId, fileList, kind = "results", findingId = null) {
  const form = new FormData();
  for (const f of fileList) form.append("files", f);
  form.append("kind", kind);
  if (findingId) form.append("finding_id", String(findingId));
  return (await apiClient.post(`/claims/${claimId}/qcc/${qccId}/files`, form)).data;
}

export async function addQccFinding(claimId, qccId, data) {
  return (await apiClient.post(`/claims/${claimId}/qcc/${qccId}/findings`, data)).data;
}

export async function updateQccFinding(claimId, qccId, findingId, data) {
  return (await apiClient.put(`/claims/${claimId}/qcc/${qccId}/findings/${findingId}`, data)).data;
}

export async function deleteQccFinding(claimId, qccId, findingId) {
  return (await apiClient.delete(`/claims/${claimId}/qcc/${qccId}/findings/${findingId}`)).data;
}

export async function removeQccFile(claimId, qccId, fileId) {
  return (await apiClient.delete(`/claims/${claimId}/qcc/${qccId}/files/${fileId}`)).data;
}

export async function listExpectedInvoiceDates(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/expected-invoice-dates`);
  return response.data;
}

export async function setExpectedInvoiceDates(claimId, startDate, percentage) {
  const response = await apiClient.post(`/claims/${claimId}/expected-invoice-dates`, {
    start_date: startDate,
    percentage,
  });
  return response.data;
}

export async function clearExpectedInvoiceDates(claimId) {
  const response = await apiClient.delete(`/claims/${claimId}/expected-invoice-dates`);
  return response.data;
}

// Row-level editing: send the FULL list of {expected_date, percentage} rows.
export async function replaceExpectedInvoiceDates(claimId, rows) {
  const response = await apiClient.put(`/claims/${claimId}/expected-invoice-dates`, { rows });
  return response.data;
}

// Marketing phone calls — worklist, contact log, subscriptions, RDR summary.
export async function getMarketingWorklist(pm = "") {
  return (await apiClient.get(`/marketing/worklist`, { params: { pm } })).data;
}
export async function getMarketingSummary() {
  return (await apiClient.get(`/marketing/summary`)).data;
}
export async function listAssessorContacts(assessorId) {
  return (await apiClient.get(`/marketing/contacts/${assessorId}`)).data;
}
export async function logAssessorContact(data) {
  return (await apiClient.post(`/marketing/contacts`, data)).data;
}
export async function subscribeAssessor(data) {
  await apiClient.post(`/marketing/subscriptions`, data);
}
export async function unsubscribeAssessor(id) {
  await apiClient.delete(`/marketing/subscriptions/${id}`);
}

// File Life report (legacy FileLifeTarget) — per-job file life + on-hold breakdown.
export async function getFileLifeReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/file-life`, { params });
  return response.data;
}

// The monthly KPI pack for one assessor company (SLA + File Life + feedback).
export async function getKpiReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/kpi`, { params });
  return response.data;
}

export async function listKpiCompanies() {
  const response = await apiClient.get(`/claims/reports/kpi/companies`);
  return response.data;
}

// Predictive Invoicing report (RDR family) — PM × month expected-invoicing matrix.
export async function getPredictiveReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/predictive-invoicing`, { params });
  return response.data;
}

// Invoicing by Month — invoiced totals per calendar month of invoice_date.
export async function getInvoicingByMonth(params = {}) {
  const response = await apiClient.get(`/claims/reports/invoicing-by-month`, { params });
  return response.data;
}

// Quote Notes: the claim's saved note lines + saving a header-template selection.
export async function listClaimQuoteNotes(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/quote-notes`);
  return response.data;
}

export async function setClaimQuoteNotes(claimId, headerTemplateIds) {
  const response = await apiClient.put(`/claims/${claimId}/quote-notes`, {
    header_template_ids: headerTemplateIds,
  });
  return response.data;
}

// Quote note templates — bespoke admin (list is also the Quote tab ticker's source).
export async function listQuoteNoteTemplates() {
  return (await apiClient.get(`/quote-note-templates`)).data;
}
export async function createQuoteNoteTemplate(data) {
  return (await apiClient.post(`/quote-note-templates`, data)).data;
}
export async function updateQuoteNoteTemplate(id, data) {
  return (await apiClient.put(`/quote-note-templates/${id}`, data)).data;
}
export async function deleteQuoteNoteTemplate(id) {
  await apiClient.delete(`/quote-note-templates/${id}`);
}
export async function reorderQuoteNoteTemplates(orderedIds) {
  return (await apiClient.post(`/quote-note-templates/reorder`, { ordered_ids: orderedIds })).data;
}

// Upgrade List (System Data tab, admins only) — the stored upgrade backlog
// with KS/KR priority ordering. Delete is system owner only.
export async function listUpgradeItems(signal) {
  return (await apiClient.get(`/upgrade-items`, { signal })).data;
}
export async function createUpgradeItem(data) {
  return (await apiClient.post(`/upgrade-items`, data)).data;
}
export async function updateUpgradeItem(id, data) {
  return (await apiClient.put(`/upgrade-items/${id}`, data)).data;
}
export async function deleteUpgradeItem(id) {
  await apiClient.delete(`/upgrade-items/${id}`);
}
export async function reorderUpgradeItems(orderedIds) {
  return (await apiClient.post(`/upgrade-items/reorder`, { ordered_ids: orderedIds })).data;
}

// The printable quote letter (data for QuoteView).
export async function getQuoteDocument(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/quote-document`);
  return response.data;
}

// System notices — the "system needs a human" pipeline (holiday horizon etc.).
export async function getSystemNotices() {
  const response = await apiClient.get(`/system-data/notices`);
  return response.data;
}

export async function getLoggedAcceptedReport(year = "") {
  const response = await apiClient.get(`/claims/reports/logged-accepted`, { params: { year } });
  return response.data;
}

export async function saveInvoiceChaseComment(invoiceId, text) {
  return (await apiClient.post(`/claims/reports/find-invoices/${invoiceId}/comment`, { text })).data;
}
export async function findInvoices(params = {}) {
  const response = await apiClient.get(`/claims/reports/find-invoices`, { params });
  return response.data;
}

export async function findOrders(params = {}) {
  const response = await apiClient.get(`/claims/reports/find-orders`, { params });
  return response.data;
}

export async function getOrderWorklist(params = {}) {
  const response = await apiClient.get(`/claims/reports/order-worklist`, { params });
  return response.data;
}

export async function getPerformanceReport(period = "this_month") {
  const response = await apiClient.get(`/claims/reports/performance`, { params: { period } });
  return response.data;
}

export async function getHsCompaniesExpiring() {
  const response = await apiClient.get(`/claims/reports/hs-companies`);
  return response.data;
}

export async function getMainHeader() {
  const response = await apiClient.get(`/claims/reports/main-header`);
  return response.data;
}

export async function getAccountsTotals() {
  const response = await apiClient.get(`/claims/reports/accounts-totals`);
  return response.data;
}

export async function getMonthlyInvoiceTotals(months = 12) {
  const response = await apiClient.get(`/claims/reports/monthly-invoice-totals`, { params: { months } });
  return response.data;
}

// --- Predefined Searches (the 1.0 report pages) ------------------------------
export async function getFeedbackReport(kind, params = {}) {
  const response = await apiClient.get(`/claims/reports/feedback/${kind}`, { params });
  return response.data;
}

export async function getImportantJobsReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/important-jobs`, { params });
  return response.data;
}

export async function getStrikeRateReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/strike-rate`, { params });
  return response.data;
}

export async function getJobScheduleReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/job-schedule`, { params });
  return response.data;
}

export async function getNonApprovedInvoicesReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/non-approved-invoices`, { params });
  return response.data;
}

export async function getOverrunsWorklist(params = {}) {
  const response = await apiClient.get(`/claims/reports/overruns`, { params });
  return response.data;
}

export async function getOverrunsPeriodReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/overruns/period`, { params });
  return response.data;
}

export async function getWorkGivenReport({ signal, ...params } = {}) {
  const response = await apiClient.get(`/claims/reports/work-given`, { params, signal });
  return response.data;
}

export async function checkOverrun(orderId) {
  const response = await apiClient.post(`/claims/reports/overruns/${orderId}/check`);
  return response.data;
}

export async function checkAllOnTargetOverruns() {
  const response = await apiClient.post(`/claims/reports/overruns/check-all-on-target`);
  return response.data;
}

// `signal` is pulled OUT of the params: axios would otherwise serialise it into
// the query string as an AbortSignal, and the abort would never take effect.
export async function getPhonebook({ signal, ...params } = {}) {
  const response = await apiClient.get(`/claims/reports/phonebook`, { params, signal });
  return response.data;
}

export async function getSlaReport(params = {}) {
  const response = await apiClient.get(`/claims/reports/sla`, { params });
  return response.data;
}

export async function getSlaReasons() {
  const response = await apiClient.get(`/claims/reports/sla-reasons`);
  return response.data;
}

export async function setSlaReason(claimId, columnKey, reasonId) {
  const response = await apiClient.put(`/claims/${claimId}/sla-reason`, {
    column_key: columnKey,
    reason_id: reasonId,
  });
  return response.data;
}

// One claim's SLA columns + current OOT reasons (Progress-screen OOT panel).
export async function getClaimSla(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/sla`);
  return response.data;
}

export async function getAcceptedStatusOptions() {
  const response = await apiClient.get(`/claims/accepted-status-options`);
  return response.data;
}

export async function changeAcceptedStatus(id, payload) {
  const response = await apiClient.post(`/claims/${id}/accepted-status`, payload);
  return response.data;
}

/** Finished. `finished: null` clears the decision. Refused while work is outstanding. */
export async function setClaimFinished(id, finished) {
  const response = await apiClient.post(`/claims/${id}/finished`, { finished });
  return response.data;
}

/** Re-open a finished job that has gained work. Never finishes anything. */
export async function recheckClaimFinished(id) {
  const response = await apiClient.post(`/claims/${id}/finished/recheck`);
  return response.data;
}

export async function rollbackClaimStatus(id, steps = 1) {
  const response = await apiClient.post(`/claims/${id}/status/rollback`, { steps });
  return response.data;
}

// --- Inspections (1-to-many) ------------------------------------------------

export async function listInspections(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/inspections`);
  return response.data;
}

export async function createInspection(claimId, data) {
  const response = await apiClient.post(`/claims/${claimId}/inspections`, data);
  return response.data;
}

export async function updateInspection(claimId, inspId, data) {
  const response = await apiClient.put(`/claims/${claimId}/inspections/${inspId}`, data);
  return response.data;
}

export async function markInspectionDone(claimId, inspId, data = {}) {
  const response = await apiClient.post(`/claims/${claimId}/inspections/${inspId}/inspected`, data);
  return response.data;
}

// Admin-only: undo an inspection (status event + completion fields) so the same
// job can be walked through again. with_photos: false keeps the photos.
export async function resetInspection(claimId, inspId, { withPhotos = true } = {}) {
  const response = await apiClient.post(
    `/claims/${claimId}/inspections/${inspId}/reset`,
    { with_photos: withPhotos }
  );
  return response.data;
}

export async function deleteInspection(claimId, inspId) {
  const response = await apiClient.delete(`/claims/${claimId}/inspections/${inspId}`);
  return response.data;
}

// --- Drag-and-drop reordering ------------------------------------------------
// One endpoint shape for every ordered list: POST the FULL list of row ids in
// their new order. `base` is the resource path under the claim, e.g. "estimates",
// "scope-lines", "pow-schedule" or `orders/${orderId}/contracts`.

export async function reorderList(claimId, base, orderedIds) {
  const response = await apiClient.post(`/claims/${claimId}/${base}/reorder`, {
    ordered_ids: orderedIds,
  });
  return response.data;
}

// --- Quote tab: Scope of Works (+ internal notes) ----------------------------
// `kind` is the route segment, e.g. "scope-lines" / "internal-notes".

// 1.0's "Create Work / Quote Request Details From Template": the job's
// request text into an empty ClaimsDesk Notes list.
export async function populateNotesFromRequest(claimId) {
  return (await apiClient.post(`/claims/${claimId}/internal-notes/from-request`)).data;
}
export async function listClaimLines(claimId, kind) {
  const response = await apiClient.get(`/claims/${claimId}/${kind}`);
  return response.data;
}

export async function createClaimLine(claimId, kind, data) {
  const response = await apiClient.post(`/claims/${claimId}/${kind}`, data);
  return response.data;
}

export async function deleteProgressEntry(claimId, entryId) {
  return (await apiClient.delete(`/claims/${claimId}/progress/${entryId}`)).data;
}
export async function getProgressContext(claimId) {
  return (await apiClient.get(`/claims/${claimId}/progress-context`)).data;
}
export async function getProgressToFromOptions(claimId) {
  return (await apiClient.get(`/claims/${claimId}/progress-to-from-options`)).data;
}

export async function updateClaimLine(claimId, kind, lineId, data) {
  const response = await apiClient.put(`/claims/${claimId}/${kind}/${lineId}`, data);
  return response.data;
}

export async function deleteClaimLine(claimId, kind, lineId) {
  const response = await apiClient.delete(`/claims/${claimId}/${kind}/${lineId}`);
  return response.data;
}

// --- Quote tab: estimate grid ----------------------------------------------

export async function listEstimates(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/estimates`);
  return response.data;
}

export async function createEstimate(claimId, data) {
  const response = await apiClient.post(`/claims/${claimId}/estimates`, data);
  return response.data;
}

export async function deleteRoomLines(claimId, roomId) {
  const response = await apiClient.post(`/claims/${claimId}/estimates/delete-room`, { room_id: roomId });
  return response.data;
}

export async function cloneEstimateLinesToRoom(claimId, estimateIds, targetRoomId, afterRoomId = null, headerTexts = null) {
  const response = await apiClient.post(`/claims/${claimId}/estimates/clone-room`, {
    estimate_ids: estimateIds,
    target_room_id: targetRoomId,
    after_room_id: afterRoomId || null,
    header_texts: headerTexts || null,
  });
  return response.data;
}

export async function updateEstimate(claimId, estId, data) {
  const response = await apiClient.put(`/claims/${claimId}/estimates/${estId}`, data);
  return response.data;
}

// Set Markup — bulk re-markup of every quote line except PC Sums (legacy
// Quotes-box control; admin/receptionist only).
export async function setEstimatesMarkup(claimId, markupPercent) {
  const response = await apiClient.post(`/claims/${claimId}/estimates/set-markup`, {
    markup_percent: markupPercent,
  });
  return response.data;
}

export async function deleteEstimate(claimId, estId) {
  const response = await apiClient.delete(`/claims/${claimId}/estimates/${estId}`);
  return response.data;
}

// Remember how a job's quote was last printed (saved at Print; the print
// modal opens from it — quotes.md 7a.10).
export async function savePrintQuoteOptions(claimId, options) {
  const response = await apiClient.put(`/claims/${claimId}/print-quote-options`, { options });
  return response.data;
}

// Auto P&G / JMF fee-line states (quotes.md §8). Reset serves both directions:
// frozen -> auto (un-edit) and removed -> auto (restore).
export async function getQuoteFees(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/quote-fees`);
  return response.data;
}

export async function resetQuoteFee(claimId, kind) {
  const response = await apiClient.post(`/claims/${claimId}/quote-fees/${kind}/reset`);
  return response.data;
}

// The per-VARIATION auto fee lines (quotes.md 8.29): one call serves Reset
// (frozen -> auto), Restore (removed -> auto) and Enable (disabled -> auto)
// for one header and kind. Returns the header's four states.
export async function resetVariationFee(claimId, vid, kind) {
  const response = await apiClient.post(`/claims/${claimId}/variations/${vid}/fees/${kind}/reset`);
  return response.data;
}

// --- Quote tab: variations (headers + costing lines) ------------------------

export async function listVariations(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/variations`);
  return response.data;
}
export async function createVariation(claimId, data) {
  const response = await apiClient.post(`/claims/${claimId}/variations`, data);
  return response.data;
}
export async function updateVariation(claimId, vid, data) {
  const response = await apiClient.put(`/claims/${claimId}/variations/${vid}`, data);
  return response.data;
}
export async function deleteVariation(claimId, vid) {
  const response = await apiClient.delete(`/claims/${claimId}/variations/${vid}`);
  return response.data;
}

export async function listAdditions(claimId) {
  const response = await apiClient.get(`/claims/${claimId}/estimate-additions`);
  return response.data;
}
export async function createAddition(claimId, data) {
  const response = await apiClient.post(`/claims/${claimId}/estimate-additions`, data);
  return response.data;
}
export async function updateAddition(claimId, aid, data) {
  const response = await apiClient.put(`/claims/${claimId}/estimate-additions/${aid}`, data);
  return response.data;
}
export async function deleteAddition(claimId, aid) {
  const response = await apiClient.delete(`/claims/${claimId}/estimate-additions/${aid}`);
  return response.data;
}

// --- Quote tab: PC sums, final quote, grand totals --------------------------

export async function pullPcSumsFromQuote(claimId) {
  return (await apiClient.post(`/claims/${claimId}/pc-sums/pull-from-quote`)).data;
}
export async function listMergeablePcSumLines(claimId) {
  return (await apiClient.get(`/claims/${claimId}/pc-sums/mergeable`)).data;
}
export async function mergePcSumsFromQuote(claimId, estimateIds) {
  return (await apiClient.post(`/claims/${claimId}/pc-sums/merge-from-quote`, { estimate_ids: estimateIds })).data;
}
export async function listPcSums(claimId) {
  return (await apiClient.get(`/claims/${claimId}/pc-sums`)).data;
}
export async function createPcSum(claimId, data) {
  return (await apiClient.post(`/claims/${claimId}/pc-sums`, data)).data;
}
export async function updatePcSum(claimId, id, data) {
  return (await apiClient.put(`/claims/${claimId}/pc-sums/${id}`, data)).data;
}
export async function deletePcSum(claimId, id) {
  return (await apiClient.delete(`/claims/${claimId}/pc-sums/${id}`)).data;
}

export async function listFinalQuote(claimId) {
  return (await apiClient.get(`/claims/${claimId}/final-quote`)).data;
}
export async function createFinalQuote(claimId, data) {
  return (await apiClient.post(`/claims/${claimId}/final-quote`, data)).data;
}
export async function updateFinalQuote(claimId, id, data) {
  return (await apiClient.put(`/claims/${claimId}/final-quote/${id}`, data)).data;
}
export async function deleteFinalQuote(claimId, id) {
  return (await apiClient.delete(`/claims/${claimId}/final-quote/${id}`)).data;
}

export async function getQuoteTotals(claimId) {
  return (await apiClient.get(`/claims/${claimId}/quote-totals`)).data;
}

// --- Quote tab: sign-off (typed / checked) ----------------------------------

export async function getQuoteSignoff(claimId) {
  return (await apiClient.get(`/claims/${claimId}/quote-signoff`)).data;
}
export async function setQuoteSignoff(claimId, which, value) {
  // which: "typed" | "checked"; value true = mark (POST), false = clear (DELETE).
  const url = `/claims/${claimId}/quote-signoff/${which}`;
  return (value ? await apiClient.post(url) : await apiClient.delete(url)).data;
}
// Assign (userId) or clear (null) who should check the typed quote.
export async function setQuotePmToCheck(claimId, userId) {
  return (
    await apiClient.put(`/claims/${claimId}/quote-signoff/pm-to-check`, { user_id: userId })
  ).data;
}

// --- Orders (payable side) --------------------------------------------------

export async function listOrders(claimId) {
  return (await apiClient.get(`/claims/${claimId}/orders`)).data;
}
export async function createOrder(claimId, data) {
  return (await apiClient.post(`/claims/${claimId}/orders`, data)).data;
}
export async function updateOrder(claimId, orderId, data) {
  return (await apiClient.put(`/claims/${claimId}/orders/${orderId}`, data)).data;
}
export async function deleteOrder(claimId, orderId) {
  return (await apiClient.delete(`/claims/${claimId}/orders/${orderId}`)).data;
}
export async function autoCreateOrders(claimId) {
  return (await apiClient.post(`/claims/${claimId}/orders/auto-create`)).data;
}
export async function getAutoCreateCheck(claimId) {
  return (await apiClient.get(`/claims/${claimId}/orders/auto-create-check`)).data;
}
export async function getCombineCandidates(claimId) {
  return (await apiClient.get(`/claims/${claimId}/orders/combine-candidates`)).data;
}
export async function combineOrders(claimId, contractorCompanyId) {
  return (await apiClient.post(`/claims/${claimId}/orders/combine`, { contractor_company_id: contractorCompanyId })).data;
}

export async function listContracts(claimId, orderId) {
  return (await apiClient.get(`/claims/${claimId}/orders/${orderId}/contracts`)).data;
}
export async function createContract(claimId, orderId, data) {
  return (await apiClient.post(`/claims/${claimId}/orders/${orderId}/contracts`, data)).data;
}
export async function updateContract(claimId, orderId, contractId, data) {
  return (await apiClient.put(`/claims/${claimId}/orders/${orderId}/contracts/${contractId}`, data)).data;
}
export async function deleteContract(claimId, orderId, contractId) {
  return (await apiClient.delete(`/claims/${claimId}/orders/${orderId}/contracts/${contractId}`)).data;
}

export async function listContractorInvoices(claimId, orderId) {
  return (await apiClient.get(`/claims/${claimId}/orders/${orderId}/invoices`)).data;
}
export async function createContractorInvoice(claimId, orderId, data) {
  return (await apiClient.post(`/claims/${claimId}/orders/${orderId}/invoices`, data)).data;
}
export async function updateContractorInvoice(claimId, orderId, invId, data) {
  return (await apiClient.put(`/claims/${claimId}/orders/${orderId}/invoices/${invId}`, data)).data;
}
export async function deleteContractorInvoice(claimId, orderId, invId) {
  return (await apiClient.delete(`/claims/${claimId}/orders/${orderId}/invoices/${invId}`)).data;
}
// The invoice document (legacy OrderInvoiceFile) — one per invoice, replaced on
// re-upload. Downloads use fileContentUrl(id) like every other file.
export async function uploadContractorInvoiceFile(claimId, orderId, invId, file) {
  const form = new FormData();
  form.append("file", file);
  return (
    await apiClient.post(`/claims/${claimId}/orders/${orderId}/invoices/${invId}/file`, form)
  ).data;
}
export async function deleteContractorInvoiceFile(claimId, orderId, invId) {
  return (await apiClient.delete(`/claims/${claimId}/orders/${orderId}/invoices/${invId}/file`)).data;
}

// --- POW (Program of Works) -------------------------------------------------

export async function listPowTasks(claimId, hideCompleted = false) {
  return (await apiClient.get(`/claims/${claimId}/pow-tasks`, { params: hideCompleted ? { hideCompleted: 1 } : {} })).data;
}
export async function createPowTask(claimId, data) {
  return (await apiClient.post(`/claims/${claimId}/pow-tasks`, data)).data;
}
export async function updatePowTask(claimId, taskId, data) {
  return (await apiClient.put(`/claims/${claimId}/pow-tasks/${taskId}`, data)).data;
}
export async function deletePowTask(claimId, taskId) {
  return (await apiClient.delete(`/claims/${claimId}/pow-tasks/${taskId}`)).data;
}

// POW Schedule checklist (legacy GPSProgressToDo) — separate from the dated tasks.
export async function listPowSchedule(claimId, hideCompleted = false) {
  return (await apiClient.get(`/claims/${claimId}/pow-schedule`, { params: hideCompleted ? { hideCompleted: 1 } : {} })).data;
}
export async function createPowScheduleItem(claimId, data) {
  return (await apiClient.post(`/claims/${claimId}/pow-schedule`, data)).data;
}
export async function updatePowScheduleItem(claimId, itemId, data) {
  return (await apiClient.put(`/claims/${claimId}/pow-schedule/${itemId}`, data)).data;
}
export async function deletePowScheduleItem(claimId, itemId) {
  return (await apiClient.delete(`/claims/${claimId}/pow-schedule/${itemId}`)).data;
}

// --- Files ------------------------------------------------------------------
const API_BASE = import.meta.env.VITE_API_URL || "/api";

// Same-origin URL for an <img>/download link. 404s gracefully when bytes are absent.
// Manual inspection attachments (1.0's InspectionPage uploaders).
// kind: "results" = the inspection's own paperwork and photos ·
//       "for_inspector" = material supplied TO the inspector.
export async function listInspectionFiles(claimId, inspectionId) {
  return (await apiClient.get(`/claims/${claimId}/inspections/${inspectionId}/files`)).data;
}
export async function uploadInspectionFiles(claimId, inspectionId, fileList, kind = "results", onUploadProgress) {
  const form = new FormData();
  for (const f of fileList) form.append("files", f);
  form.append("kind", kind);
  return (
    await transfer(
      (config) =>
        apiClient.post(`/claims/${claimId}/inspections/${inspectionId}/files`, form, config),
      { onProgress: onUploadProgress, direction: "up" }
    )
  ).data;
}
export async function deleteInspectionFile(claimId, inspectionId, fileId) {
  return (await apiClient.delete(`/claims/${claimId}/inspections/${inspectionId}/files/${fileId}`)).data;
}

export function fileContentUrl(fileId, { download = false } = {}) {
  return `${API_BASE}/files/${fileId}/content${download ? "?download=1" : ""}`;
}
export async function listClaimFiles(claimId, category) {
  return (await apiClient.get(`/claims/${claimId}/files`, { params: category ? { category } : {} })).data;
}
// An external link on the Files tab (YouTube walkthroughs etc.) — a file row
// whose bytes live elsewhere. https only; the server validates.
export async function addClaimLink(claimId, { url, name, category = null }) {
  return (await apiClient.post(`/claims/${claimId}/links`, { url, name, category })).data;
}
export async function uploadClaimFiles(claimId, fileList, { fileType = "", prefix = "" } = {}, onUploadProgress) {
  const form = new FormData();
  // Text fields BEFORE the files so multer has them parsed when handling the upload.
  if (fileType) form.append("fileType", fileType);
  if (prefix) form.append("prefix", prefix);
  for (const f of fileList) form.append("files", f);
  return (
    await transfer((config) => apiClient.post(`/claims/${claimId}/files`, form, config), {
      onProgress: onUploadProgress,
      direction: "up",
    })
  ).data;
}
// Zip a claim's files. `fileIds` empty = everything. Returns the blob plus what
// the server put in the headers, because a binary body cannot tell you its own
// name or that it had to skip anything.
export async function downloadClaimArchive(claimId, fileIds = [], onProgress) {
  const response = await transfer(
    (config) =>
      apiClient.post(
        `/claims/${claimId}/files/archive`,
        { file_ids: fileIds },
        { ...config, responseType: "blob" }
      ),
    {
      // Real transfer progress, 0..1, or null when the response carries no
      // length to measure against. On a rural connection the archive is built in
      // under a second and then spends a long time coming down the wire, and
      // without this there is nothing on screen saying so.
      //
      // NULL MEANS "UNKNOWN", NOT "FINISHED". Whether the transfer is still
      // running is the caller's own business — it knows when it awaited and when
      // that returned — and a caller that infers it from this number instead
      // reads a length-less response as a completed one.
      onProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : null),
      direction: "down",
    }
  );
  return {
    blob: response.data,
    filename: response.headers["x-archive-filename"] || "files.zip",
    files: Number(response.headers["x-archive-files"] || 0),
    missing: Number(response.headers["x-archive-missing"] || 0),
  };
}

export async function deleteClaimFile(claimId, fileId) {
  return (await apiClient.delete(`/claims/${claimId}/files/${fileId}`)).data;
}
// Rename a file (the server keeps the extension in step with the file's type).
export async function renameClaimFile(claimId, fileId, name) {
  return (await apiClient.patch(`/claims/${claimId}/files/${fileId}`, { original_filename: name })).data;
}
export async function resetClaimFiles(claimId) {
  return (await apiClient.delete(`/claims/${claimId}/files`)).data;
}
// Admin utility: backfill this claim's metadata-only files with bytes from GPSWebHelper.
export async function backfillClaimFilesFromGps(claimId) {
  return (await apiClient.post(`/claims/${claimId}/files/backfill`)).data;
}

// --- Progress Claims --------------------------------------------------------

export async function listProgressClaims(claimId) {
  return (await apiClient.get(`/claims/${claimId}/progress-claims`)).data;
}
export async function getProgressClaim(claimId, pcId) {
  return (await apiClient.get(`/claims/${claimId}/progress-claims/${pcId}`)).data;
}
export async function createProgressClaim(claimId) {
  return (await apiClient.post(`/claims/${claimId}/progress-claims`)).data;
}
export async function updateProgressLine(claimId, pcId, lineId, data) {
  return (await apiClient.put(`/claims/${claimId}/progress-claims/${pcId}/lines/${lineId}`, data)).data;
}
export async function progressSetAll100(claimId, pcId) {
  return (await apiClient.post(`/claims/${claimId}/progress-claims/${pcId}/set-all-100`)).data;
}
export async function progressRefresh(claimId, pcId) {
  return (await apiClient.post(`/claims/${claimId}/progress-claims/${pcId}/refresh`)).data;
}
export async function progressFinish(claimId, pcId) {
  return (await apiClient.post(`/claims/${claimId}/progress-claims/${pcId}/finish`)).data;
}
export async function deleteProgressClaim(claimId, pcId) {
  return (await apiClient.delete(`/claims/${claimId}/progress-claims/${pcId}`)).data;
}
export async function getProgressClaimInvoice(claimId, pcId) {
  return (await apiClient.get(`/claims/${claimId}/progress-claims/${pcId}/invoice`)).data;
}

export async function listProgressFlags() {
  const response = await apiClient.get("/claims/progress-flags");
  return response.data;
}

export async function listProgress(claimId, flag) {
  const response = await apiClient.get(`/claims/${claimId}/progress`, {
    params: flag ? { flag } : {},
  });
  return response.data;
}

export async function createProgress(claimId, data) {
  const response = await apiClient.post(`/claims/${claimId}/progress`, data);
  return response.data;
}

// Client-contact quick entry: type = "initial" | "go_ahead", result = preset text.
export async function createClientContact(claimId, type, result) {
  const response = await apiClient.post(`/claims/${claimId}/progress/client-contact`, {
    type,
    result,
  });
  return response.data;
}

export async function updateProgress(claimId, entryId, data) {
  const response = await apiClient.put(`/claims/${claimId}/progress/${entryId}`, data);
  return response.data;
}
export async function attachProgressFile(claimId, entryId, file) {
  const form = new FormData();
  form.append("file", file);
  return (await apiClient.post(`/claims/${claimId}/progress/${entryId}/file`, form)).data;
}
export async function removeProgressFile(claimId, entryId) {
  return (await apiClient.delete(`/claims/${claimId}/progress/${entryId}/file`)).data;
}

// --- Invoices ---------------------------------------------------------------
export async function listInvoices(claimId) {
  return (await apiClient.get(`/claims/${claimId}/invoices`)).data;
}
export async function getInvoice(claimId, invoiceId) {
  return (await apiClient.get(`/claims/${claimId}/invoices/${invoiceId}`)).data;
}
export async function createInvoiceFromProgressClaim(claimId, progressClaimId) {
  return (await apiClient.post(`/claims/${claimId}/invoices`, { source: "progress_claim", progressClaimId })).data;
}
export async function updateInvoice(claimId, invoiceId, data) {
  return (await apiClient.put(`/claims/${claimId}/invoices/${invoiceId}`, data)).data;
}
// "Mark as sent", not "send": Claims Desk cannot send email, so this records the
// user's assertion that the invoice went out. Sending freezes the DOCUMENT half
// of the invoice (lines, money, dates, photographs); the LEDGER half — paid,
// disputed, comments — stays editable forever.
export async function markInvoiceSent(claimId, invoiceId) {
  return (await apiClient.post(`/claims/${claimId}/invoices/${invoiceId}/sent`)).data;
}
export async function reopenInvoice(claimId, invoiceId) {
  return (await apiClient.post(`/claims/${claimId}/invoices/${invoiceId}/reopen`)).data;
}
export async function listInvoicePhotos(claimId, invoiceId) {
  return (await apiClient.get(`/claims/${claimId}/invoices/${invoiceId}/photos`)).data;
}
export async function setInvoicePhotos(claimId, invoiceId, fileIds) {
  return (await apiClient.put(`/claims/${claimId}/invoices/${invoiceId}/photos`, { file_ids: fileIds })).data;
}
/** Per-insurer invoice requirements + the completion notes to print. */
export async function getInvoiceEvidence(claimId) {
  return (await apiClient.get(`/claims/${claimId}/invoice-evidence`)).data;
}
export async function voidInvoice(claimId, invoiceId) {
  return (await apiClient.delete(`/claims/${claimId}/invoices/${invoiceId}`)).data;
}
export async function getDoAndChargeSummary(claimId) {
  return (await apiClient.get(`/claims/${claimId}/do-and-charge-summary`)).data;
}
export async function replaceDoAndChargeInvoice(claimId, invoiceId, options) {
  return (await apiClient.post(`/claims/${claimId}/invoices/${invoiceId}/replace-do-and-charge`, { options })).data;
}
export async function getInvoiceCreationState(claimId) {
  return (await apiClient.get(`/claims/${claimId}/invoices-creation-state`)).data;
}
export async function createInvoiceFromQuote(claimId) {
  return (await apiClient.post(`/claims/${claimId}/invoices`, { source: "quote" })).data;
}
export async function replaceQuotedInvoice(claimId, invoiceId) {
  return (await apiClient.post(`/claims/${claimId}/invoices/${invoiceId}/replace-quoted`)).data;
}
export async function createExcessInvoice(claimId) {
  return (await apiClient.post(`/claims/${claimId}/invoices`, { source: "excess" })).data;
}
export async function createManualInvoice(claimId, options) {
  return (await apiClient.post(`/claims/${claimId}/invoices`, { source: "manual", options })).data;
}
export async function createDoAndChargeInvoice(claimId, options) {
  return (await apiClient.post(`/claims/${claimId}/invoices`, { source: "do_and_charge", options })).data;
}

// --- Settings (claim numbering) ---------------------------------------------

export async function getNextClaimNumber() {
  const response = await apiClient.get("/settings/claim-number");
  return response.data.next_claim_number;
}


export default apiClient;

// Quote Templates — the "Create ... From Template" buttons on the Quote tab,
// managed in System Data (services/quoteTemplates.js).
export async function listQuoteTemplates() {
  return (await apiClient.get(`/quote-templates`)).data;
}
export async function listQuoteTemplateLines(templateId) {
  return (await apiClient.get(`/quote-templates/${templateId}/lines`)).data;
}
export async function createQuoteTemplate(data) {
  return (await apiClient.post(`/quote-templates`, data)).data;
}
export async function updateQuoteTemplate(id, data) {
  return (await apiClient.put(`/quote-templates/${id}`, data)).data;
}
export async function deleteQuoteTemplate(id) {
  await apiClient.delete(`/quote-templates/${id}`);
}
export async function addQuoteTemplateLine(templateId, data) {
  return (await apiClient.post(`/quote-templates/${templateId}/lines`, data)).data;
}
export async function updateQuoteTemplateLine(lineId, data) {
  return (await apiClient.put(`/quote-templates/lines/${lineId}`, data)).data;
}
export async function deleteQuoteTemplateLine(lineId) {
  await apiClient.delete(`/quote-templates/lines/${lineId}`);
}
export async function reorderQuoteTemplateLines(templateId, orderedIds) {
  return (await apiClient.post(`/quote-templates/${templateId}/lines/reorder`, { ordered_ids: orderedIds })).data;
}
// Fills an EMPTY list on a claim from a template: either a named one
// (template_id — the priced buttons) or the one resolved from the job's type.
export async function copyQuoteTemplate(id, jobTypeId) {
  return (await apiClient.post(`/quote-templates/${id}/copy`, { job_type_id: jobTypeId })).data;
}
export async function applyQuoteTemplate({ claim_id, target, template_id }) {
  return (await apiClient.post(`/quote-templates/apply`, { claim_id, target, template_id })).data;
}

// --- After-hours scheduler (System Data → Scheduler, owner only) ---------------
export async function getSchedulerState() {
  return (await apiClient.get(`/scheduler/state`)).data;
}
export async function setSchedulerConfig(patch) {
  return (await apiClient.put(`/scheduler/config`, patch)).data;
}
export async function setSchedulerTask(key, enabled) {
  return (await apiClient.put(`/scheduler/tasks/${key}`, { enabled })).data;
}
export async function runSchedulerTask(key) {
  return (await apiClient.post(`/scheduler/run/${key}`)).data;
}
export async function clearSchedulerRunLog() {
  return (await apiClient.post(`/scheduler/run-log/clear`)).data;
}

// Where the archive site lives (owner setting; empty = no Archive button).
export async function getArchiveUrl() {
  return (await apiClient.get(`/settings/archive-url`)).data?.url || "";
}

// Where the OTHER app lives — { url } is the archive, { live_url } is live.
// One request answers it for whichever app is asking (owner settings; empty
// means the button is simply not shown). The archive shows the Live button.
export async function getSiteLinks() {
  const d = (await apiClient.get(`/settings/archive-url`)).data || {};
  return { archive: d.url || "", live: d.live_url || "" };
}

// --- Office Expenses (docs/business-rules/office-expenses.md) ---------------
// GPS's own supplier bills with an approval trail — a register, not a job.
export async function listOfficeExpenses(params = {}, signal = undefined) {
  const response = await apiClient.get(`/office-expenses`, { params, signal });
  return response.data;
}
export async function listOfficeExpenseCategories(signal = undefined) {
  const response = await apiClient.get(`/office-expenses/categories`, { signal });
  return response.data;
}
export async function createOfficeExpense(data) {
  const response = await apiClient.post(`/office-expenses`, data);
  return response.data;
}
export async function updateOfficeExpense(id, data) {
  const response = await apiClient.put(`/office-expenses/${id}`, data);
  return response.data;
}
export async function deleteOfficeExpense(id) {
  const response = await apiClient.delete(`/office-expenses/${id}`);
  return response.data;
}
// stage: approved | unapproved | with_accounts | not_with_accounts | paid | unpaid
export async function setOfficeExpenseStage(id, stage, date = null) {
  const response = await apiClient.post(`/office-expenses/${id}/stage`, { stage, date });
  return response.data;
}
// expenseId null = the register's own notes.
export async function listOfficeExpenseNotes(expenseId = null, signal = undefined) {
  const url = expenseId == null ? `/office-expenses/notes` : `/office-expenses/${expenseId}/notes`;
  const response = await apiClient.get(url, { signal });
  return response.data;
}
export async function addOfficeExpenseNote(expenseId, note) {
  const url = expenseId == null ? `/office-expenses/notes` : `/office-expenses/${expenseId}/notes`;
  const response = await apiClient.post(url, { note });
  return response.data;
}
export function officeExpensesCsvUrl(params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return `/api/office-expenses/export.csv${qs ? "?" + qs : ""}`;
}
// The bill's documents.
export async function uploadOfficeExpenseFile(expenseId, file) {
  const form = new FormData();
  form.append("file", file);
  const response = await apiClient.post(`/office-expenses/${expenseId}/files`, form);
  return response.data;
}
export async function deleteOfficeExpenseFile(expenseId, fileId) {
  const response = await apiClient.delete(`/office-expenses/${expenseId}/files/${fileId}`);
  return response.data;
}
export function officeExpenseFileUrl(expenseId, fileId) {
  return `/api/office-expenses/${expenseId}/files/${fileId}/download`;
}

// Company documents — liability / H&S / pre-qualification (companies.md).
export async function listCompanyFiles(companyId, { signal } = {}) {
  const response = await apiClient.get(`/companies/${companyId}/files`, { signal });
  return response.data;
}
export async function uploadCompanyFile(companyId, docType, file, expiresOn = null) {
  const form = new FormData();
  form.append("doc_type", docType);
  if (expiresOn) form.append("expires_on", expiresOn);
  form.append("file", file);
  const response = await apiClient.post(`/companies/${companyId}/files`, form);
  return response.data;
}
export async function updateCompanyFile(companyId, fileId, patch) {
  const response = await apiClient.patch(`/companies/${companyId}/files/${fileId}`, patch);
  return response.data;
}
export async function deleteCompanyFile(companyId, fileId) {
  const response = await apiClient.delete(`/companies/${companyId}/files/${fileId}`);
  return response.data;
}
export function companyFileUrl(companyId, fileId) {
  return `/api/companies/${companyId}/files/${fileId}/download`;
}

// Who's online (owner only; presence.md).
export async function getPresenceNow({ signal } = {}) {
  const response = await apiClient.get("/presence/now", { signal });
  return response.data;
}
export async function getPresenceDay(date, { signal } = {}) {
  const response = await apiClient.get("/presence/day", { params: { date }, signal });
  return response.data;
}
